import {
  ShareCounts,
  Sharing
} from '@ta-interaktiv/types/lib/www.tenant.ch/disco-api/v1/Sharing'
import { handleRequest } from './handleRequest'
import { catchError as catchErrorTemplate } from './catchError'
import { HTTPError } from './HTTPError'
import { getPropertyFromPromisedObject } from './getSingleProperty'
import { countsEndpoint } from './sharingApiEndpoint'

const defaultShareCounts: ShareCounts = {
  facebook: 0,
  twitter: 0,
  whatsapp: 0,
  email: 0,
  native: 0,
  linkedin: 0,
  total: 0
}

const defaultOptions = {
  subdomain: 'www',
  version: 'v1'
}

function handleData(articleId: string | number) {
  return (data: Sharing) => {
    return data.counts
  }
}

function catchError(subclass: Function) {
  return catchErrorTemplate('getSharingCounts', subclass, defaultShareCounts)
}

/**
 * Cache for fetch promises – this way we can repeatedly call these functions,
 * while only one fetch request goes out, reducing the strain on the APIs.
 *
 * Use `clearCache()` and `injectDataInCache()` to manipulate this cache, so
 * it is possible to change the implementation details later on.
 */
const shareCountPromiseCache = new Map<string | number, Promise<ShareCounts>>()

/**
 * For testing purposes only – clears the cache.
 */
export function clearCache() {
  shareCountPromiseCache.clear()
}

/**
 * For testing purposes only – injects data into the cache
 */
export function injectDataInCache(key: string | number, counts: ShareCounts) {
  shareCountPromiseCache.set(key, Promise.resolve(counts))
}

/**
 * Get the share counts for an article on a specific domain.
 *
 * In case of a failure, zeroes across the board are being returned, and *no*
 * exception is thrown.
 *
 * @param domainName - The domain name, without the top level domain (i.e. `tagesanzeiger` or `derbund`)
 * @param articleId - The article ID to get the shares for
 */
export function getSharingCounts(
  domainName: string,
  articleId: string | number
): Promise<ShareCounts> {
  if (shareCountPromiseCache.has(articleId)) {
    return shareCountPromiseCache.get(articleId) as Promise<ShareCounts>
  }

  const promise = fetch(countsEndpoint(domainName, articleId, defaultOptions))
    .then(handleRequest<Sharing>())
    .then(handleData(articleId))
    .catch(reason => {
      handleData(articleId)({
        counts: defaultShareCounts,
        articleId: articleId.toString()
      })
      throw reason
    })
    .catch(catchError(HTTPError))
    .catch(catchError(Error))

  shareCountPromiseCache.set(articleId, promise)
  return promise
}

/**
 * Get total shares for an article
 */
export function getTotalShareCount(
  domainName: string,
  articleId: string | number
) {
  return getPropertyFromPromisedObject(
    getSharingCounts(domainName, articleId),
    'total'
  )
}
